// frontend_react/src/pages/calendar/MyCalendar.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  CalendarDays,
  Clock3,
  ExternalLink,
  RefreshCw,
  Upload,
  Download,
  UserRound,
  UsersRound,
  Info,
  X,
} from "lucide-react";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import rrulePlugin from "@fullcalendar/rrule";

import {
  exportIcs,
  importIcs,
  fetchEvents as apiFetchEvents,
  getCalendarEvents,
  updateEvent,
  fetchEventRegistrations,
} from "../../services/calendarApi";

import logger from "../../utils/logger";

import { useAdminScope } from "../../context/AdminScopeContext";
import { AdminButton } from "../admin/action_engine";
import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../admin/ui_engine";
import {
  useOptionalAdminRuntime,
} from "../admin/runtime_engine";
import {
  AdminActions,
  AdminSection,
  AdminSectionBadge,
  AdminSectionHeader,
  AdminSectionTitle,
  AdminTable,
  AdminTableViewport,
} from "../admin/section_engine";
import {
  resolveEventCategoryPresentation,
} from "./eventCategoryPresentation";
import {
  eventInstantToCalendarValue,
  calendarDateToLocalApi,
  calendarDateToDateInput,
  formatEventDate,
  formatEventTime,
} from "./eventTemporal";

export default function MyCalendar({ variant = "page", withNavbar = false }) {
  const [events, setEvents] = useState([]);
  const [crmEvents, setCrmEvents] = useState([]);
  const [crmLoading, setCrmLoading] = useState(false);
  const [activeCalendarView, setActiveCalendarView] =
    useState("dayGridMonth");
  const {
    selectedCompanyUuid,
    selectedActivityUuid,
  } = useAdminScope();

  const adminRuntime =
    useOptionalAdminRuntime();

  const closePanel =
    adminRuntime?.closePanel;

  const [selectedCrmEvent, setSelectedCrmEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [regsLoading, setRegsLoading] = useState(false);
  const [regsError, setRegsError] = useState("");

  const calendarRef = useRef(null);
  const rangeRef = useRef({ start: "", end: "" });

  const navigate = useNavigate();

  const toNaiveIso = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}`;
  };

  const normalizeLocal = (value) =>
    value && value.length === 16 ? `${value}:00` : value;

  const toLocalInput = (value) => (value ? value.slice(0, 16) : "");

  const fetchEvents = async (start, end) => {
    try {
      const res = await apiFetchEvents(start, end, {
        companyUuid: selectedCompanyUuid,
        activityUuid: selectedActivityUuid,
      });
      const payload = res.data;
      const arr = Array.isArray(payload) ? payload : payload?.events ?? [];
      setEvents(arr);
    } catch (err) {
      logger.error(err);
      setEvents([]);
    }
  };

  const refreshCrm = async () => {
    setCrmLoading(true);

    try {
      const res = await getCalendarEvents({
        companyUuid: selectedCompanyUuid,
        activityUuid: selectedActivityUuid,
      });
      setCrmEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      logger.error(err);
      setCrmEvents([]);
    } finally {
      setCrmLoading(false);
    }
  };

  const fetchRegistrations = async (eventUuid) => {
    setRegsLoading(true);
    setRegsError("");

    try {
      const res = await fetchEventRegistrations(eventUuid, {
        companyUuid: selectedCompanyUuid,
        activityUuid: selectedActivityUuid,
      });
      setRegistrations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      logger.error(err);
      setRegistrations([]);
      setRegsError("Impossible de charger les inscriptions.");
    } finally {
      setRegsLoading(false);
    }
  };

  useEffect(() => {
    setSelectedCrmEvent(null);
    setRegistrations([]);
    refreshCrm();
  }, [
    selectedCompanyUuid,
    selectedActivityUuid,
  ]);

  const calendarEvents = useMemo(
    () =>
      events.map((event) => {
        const presentation =
          resolveEventCategoryPresentation(event.event_category);

        return {
          title: event.title,
          start: eventInstantToCalendarValue(
            event.start_timestamp,
            event.timezone || "Europe/Paris",
            Boolean(event.is_all_day)
          ),
          end: eventInstantToCalendarValue(
            event.end_timestamp,
            event.timezone || "Europe/Paris",
            Boolean(event.is_all_day)
          ),
          allDay: event.is_all_day,
          rrule: event.recurrence_rule
            ? { rule: event.recurrence_rule }
            : undefined,
          backgroundColor: presentation.background,
          borderColor: presentation.color,
          textColor: "#ffffff",
          extendedProps: {
            uuid: event.uuid,
            description: event.description,
            recurrence_rule: event.recurrence_rule,
            timezone: event.timezone,
            conference_url: event.conference_url,
            event_category: event.event_category,
            event_category_label: presentation.label,
            event_category_color: presentation.color,
          },
        };
      }),
    [events]
  );

  const crmEventsSorted = useMemo(() => {
    return [...crmEvents].sort(
      (a, b) => new Date(b.start_timestamp) - new Date(a.start_timestamp)
    );
  }, [crmEvents]);

  const handleDatesSet = (arg) => {
    const start = toNaiveIso(arg.start);
    const end = toNaiveIso(arg.end);

    setActiveCalendarView(
      arg.view?.type || "dayGridMonth"
    );

    rangeRef.current = { start, end };
    fetchEvents(start, end);
  };

  const handleSelect = (info) => {
    if (info.jsEvent?.target?.closest(".fc-event")) return;

    navigate("/event/create/edit", {
      state: {
        mode: "create",
        startDate: toLocalInput(toNaiveIso(info.start)),
        endDate: info.end ? toLocalInput(toNaiveIso(info.end)) : "",
      },
    });
  };

  const handleEventClick = (info) => {
    info.jsEvent.preventDefault();
    info.jsEvent.stopPropagation();

    const uuid = info.event.extendedProps?.uuid;
    if (!uuid) return;

    navigate(`/event/${uuid}/edit`);
  };

  const handleExportIcs = async () => {
    try {
      const res = await exportIcs({
        companyUuid: selectedCompanyUuid,
        activityUuid: selectedActivityUuid,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));

      const link = document.createElement("a");
      link.href = url;
      link.download = "calendar.ics";
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error(err);
    }
  };

  const handleImportIcs = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();

      await importIcs(text, {
        companyUuid: selectedCompanyUuid,
        activityUuid: selectedActivityUuid,
      });
      await fetchEvents(rangeRef.current.start, rangeRef.current.end);
      await refreshCrm();

      event.target.value = "";
    } catch (err) {
      logger.error(err);
    }
  };

  const openPublicRegister = (eventUuid) => {
    window.open(
      `/event/${eventUuid}/register`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleCloseWorkspace = () => {
    if (
      variant === "sidePanel"
      && typeof closePanel === "function"
    ) {
      closePanel();
      return;
    }

    navigate(-1);
  };

  return (
    <MainGrid $variant={variant}>
      <AdminSection>
        <CalendarHeader>
          <HeaderCopy>
            <AdminSectionBadge>Agenda</AdminSectionBadge>

            <HeaderTitleRow>
              <AdminSectionTitle>
                Planning général
              </AdminSectionTitle>

              <CalendarHeaderActions>
                <AdminButton
                  type="button"
                  variant="ghost"
                  size="small"
                  onClick={handleExportIcs}
                >
                  <Download size={15} aria-hidden="true" />
                  Export .ICS
                </AdminButton>

                <ImportLabel>
                  <Upload size={15} aria-hidden="true" />
                  Import .ICS
                  <input
                    type="file"
                    accept=".ics"
                    onChange={handleImportIcs}
                  />
                </ImportLabel>

                <HeaderIconButton
                  type="button"
                  variant="ghost"
                  size="small"
                  onClick={handleCloseWorkspace}
                  aria-label="Fermer le calendrier"
                  title="Fermer"
                >
                  <X size={17} aria-hidden="true" />
                </HeaderIconButton>
              </CalendarHeaderActions>
            </HeaderTitleRow>

            <HeaderDescription>
              Visualisez et gérez tous les événements planifiés.
            </HeaderDescription>
          </HeaderCopy>
        </CalendarHeader>

          <CalendarCard>
            <FullCalendar
              ref={calendarRef}
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                interactionPlugin,
                rrulePlugin,
              ]}
              locale="fr"
              timeZone="local"
              firstDay={1}
              initialView="dayGridMonth"
              weekNumbers
              weekText="S"
              height={
                activeCalendarView === "dayGridMonth"
                  ? "auto"
                  : "calc(100vh - 250px)"
              }
              contentHeight={
                activeCalendarView === "dayGridMonth"
                  ? "auto"
                  : undefined
              }
              dayMaxEvents={3}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              buttonText={{
                today: "Aujourd’hui",
                month: "Mois",
                week: "Semaine",
                day: "Jour",
              }}
              events={calendarEvents}
              selectable
              selectMirror
              editable
              select={handleSelect}
              unselectAuto
              eventClick={handleEventClick}
              datesSet={handleDatesSet}
              eventDrop={(info) => {
                const isAllDay = Boolean(info.event.allDay);
                const timezone =
                  info.event.extendedProps?.timezone || "Europe/Paris";

                return updateEvent(
                  info.event.extendedProps.uuid,
                  {
                    start_timestamp: isAllDay
                      ? calendarDateToDateInput(info.event.start)
                      : calendarDateToLocalApi(info.event.start),
                    end_timestamp: info.event.end
                      ? (
                          isAllDay
                            ? calendarDateToDateInput(info.event.end)
                            : calendarDateToLocalApi(info.event.end)
                        )
                      : null,
                    is_all_day: isAllDay,
                    timezone,
                  },
                  {
                    companyUuid: selectedCompanyUuid,
                    activityUuid: selectedActivityUuid,
                  }
                ).then(refreshCrm);
              }}
              eventResize={(info) => {
                const isAllDay = Boolean(info.event.allDay);
                const timezone =
                  info.event.extendedProps?.timezone || "Europe/Paris";

                return updateEvent(
                  info.event.extendedProps.uuid,
                  {
                    end_timestamp: info.event.end
                      ? (
                          isAllDay
                            ? calendarDateToDateInput(info.event.end)
                            : calendarDateToLocalApi(info.event.end)
                        )
                      : null,
                    is_all_day: isAllDay,
                    timezone,
                  },
                  {
                    companyUuid: selectedCompanyUuid,
                    activityUuid: selectedActivityUuid,
                  }
                ).then(refreshCrm);
              }}
            />
          </CalendarCard>


      </AdminSection>

      <AdminSection>
        <AdminSectionHeader>
          <HeaderCopy>
            <AdminSectionBadge>Événements</AdminSectionBadge>
            <AdminSectionTitle>Événements</AdminSectionTitle>
            <HeaderDescription>
              Inscriptions & liens publics
            </HeaderDescription>
          </HeaderCopy>

          <IconButton
            type="button"
            onClick={refreshCrm}
            disabled={crmLoading}
            aria-label="Rafraîchir"
          >
            <RefreshCw
              size={18}
              aria-hidden="true"
              className={crmLoading ? "is-loading" : ""}
            />
          </IconButton>
        </AdminSectionHeader>

          <EventsList>
            {crmLoading && (
              <AdminLoadingState
                message="Chargement des événements..."
                compact
              />
            )}

            {!crmLoading && crmEventsSorted.length === 0 && (
              <AdminEmptyState
                message="Aucun événement."
                compact
              />
            )}

            {!crmLoading &&
              crmEventsSorted.map((event) => (
                <EventCard
                  key={event.uuid}
                  $active={selectedCrmEvent?.uuid === event.uuid}
                  $accent={
                    resolveEventCategoryPresentation(
                      event.event_category
                    ).color
                  }
                  onClick={() => {
                    setSelectedCrmEvent(event);
                    fetchRegistrations(event.uuid);
                  }}
                >
                  <EventTopLine>
                    <EventTitleGroup>
                      <CategoryDot
                        $color={
                          resolveEventCategoryPresentation(
                            event.event_category
                          ).color
                        }
                      />
                      <strong>{event.title}</strong>
                    </EventTitleGroup>

                    <ParticipantsBadge
                      aria-label={`${
                        event.total_participants ?? 0
                      } participant(s)`}
                    >
                      <UsersRound size={15} aria-hidden="true" />
                      {event.total_participants ?? 0}
                    </ParticipantsBadge>
                  </EventTopLine>

                  

                  <EventDate>
                    <CalendarDays size={16} aria-hidden="true" />
                    {formatEventDate(
                      event.start_timestamp,
                      event.timezone || "Europe/Paris"
                    )}
                  </EventDate>

                  <EventTime>
                    <Clock3 size={16} aria-hidden="true" />
                    {event.is_all_day
                      ? "Toute la journée"
                      : (
                          <>
                            {formatEventTime(
                              event.start_timestamp,
                              event.timezone || "Europe/Paris"
                            )}
                            {" → "}
                            {formatEventTime(
                              event.end_timestamp,
                              event.timezone || "Europe/Paris"
                            )}
                          </>
                        )}
                  </EventTime>

                  <SecondaryButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPublicRegister(event.uuid);
                    }}
                  >
                    Lien d’inscription
                    <ExternalLink size={15} aria-hidden="true" />
                  </SecondaryButton>
                </EventCard>
              ))}

            {selectedCrmEvent && (
              <RegistrationsCard>
                <RegistrationsHeading>
                  <span>Inscriptions</span>
                  <RegistrationCount>
                    {registrations.length}
                  </RegistrationCount>
                </RegistrationsHeading>

                <SelectedEventTitle>
                  {selectedCrmEvent.title}
                </SelectedEventTitle>

                {regsLoading && (
                  <AdminLoadingState
                    message="Chargement des inscriptions..."
                    compact
                  />
                )}

                {!regsLoading && regsError && (
                  <AdminErrorState
                    message={regsError}
                    compact
                  />
                )}

                {!regsLoading &&
                  !regsError &&
                  registrations.length === 0 && (
                    <AdminEmptyState
                      message="Aucune inscription."
                      compact
                    />
                  )}

                {!regsLoading &&
                  !regsError &&
                  registrations.length > 0 && (
                    <AdminTableViewport>
                      <AdminTable>
                        <thead>
                          <tr>
                            <th scope="col">Participants</th>
                            <th scope="col">Contact</th>
                          </tr>
                        </thead>

                        <tbody>
                          {registrations.map((registration) => (
                            <tr key={registration.uuid}>
                              <td>
                                <RegistrationParticipants>
                                  <UserRound
                                    size={15}
                                    aria-hidden="true"
                                  />
                                  <span>
                                    {registration.nb_participants} participant(s)
                                  </span>
                                </RegistrationParticipants>
                              </td>

                              <td>
                                <RegistrationContact>
                                  {registration.contact_uuid}
                                </RegistrationContact>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </AdminTable>
                    </AdminTableViewport>
                  )}
              </RegistrationsCard>
            )}

          </EventsList>

          <ContextualHint>
            <Info size={20} aria-hidden="true" />
            <span>
              Cliquez sur un événement dans le calendrier
              pour consulter ou modifier ses détails.
            </span>
          </ContextualHint>
      </AdminSection>
    </MainGrid>
  );
}


const MainGrid = styled.main`
  display: grid;
  gap: 0.75rem;
  height: calc(100vh - 32px);
  overflow: hidden;

  ${({ $variant }) =>
    $variant === "sidePanel"
      ? `
        grid-template-columns: 1fr;
      `
      : `
        grid-template-columns:
          minmax(0, 3.25fr)
          minmax(330px, 1.18fr);
      `}

  > section {
    min-width: 0;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    height: auto;
    overflow: visible;
  }
`;

const CalendarCard = styled.div`
  min-height: 0;
  height: fit-content;
  padding: 0.85rem 1rem 1rem;
  border-radius: 14px;
  background:
    linear-gradient(
      180deg,
      rgba(14, 24, 41, 0.88),
      rgba(13, 23, 39, 0.96)
    );
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;

  .fc {
    height: auto;
    color: #f7f7f7;
    font-size: 0.84rem;
  }

  .fc-theme-standard td,
  .fc-theme-standard th {
    border-color: rgba(255, 255, 255, 0.11);
  }

  .fc-theme-standard .fc-scrollgrid {
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-collapse: separate;
    border-spacing: 0;
  }

  .fc-daygrid .fc-scroller,
  .fc-daygrid .fc-scroller-liquid-absolute {
    position: relative !important;
    overflow: visible !important;
    height: auto !important;
  }

  .fc-daygrid-body,
  .fc-daygrid-body table {
    width: 100% !important;
  }

  .fc-scrollgrid-section-header > *,
  .fc-col-header,
  .fc-col-header-cell {
    background: rgba(8, 17, 31, 0.92) !important;
  }

  .fc-col-header-cell {
    border-color: rgba(255, 255, 255, 0.11) !important;
  }

  .fc-col-header-cell-cushion {
    padding: 0.45rem 0.25rem;
    color: rgba(255, 255, 255, 0.88) !important;
    font-weight: 750;
  }

  .fc-daygrid-day-number {
    padding: 0.45rem;
    color: rgba(255, 255, 255, 0.92);
    font-weight: 650;
  }

  .fc-daygrid-week-number {
    color: rgba(255, 255, 255, 0.88);
    background: transparent;
    font-size: 0.72rem;
  }

  .fc-day-other .fc-daygrid-day-number {
    color: rgba(255, 255, 255, 0.34);
  }

  .fc-day-today {
    background: rgba(114, 94, 255, 0.10) !important;
  }

  .fc-toolbar {
    margin-bottom: 1rem !important;
    gap: 0.75rem;
  }

  .fc-toolbar-title {
    color: #f7f7f7;
    font-size: 1.28rem !important;
    font-weight: 850;
    letter-spacing: -0.025em;
  }

  .fc-button-primary {
    min-height: 34px;
    padding: 0.38rem 0.72rem !important;
    border: 1px solid rgba(255, 255, 255, 0.18) !important;
    border-radius: 8px !important;
    background: transparent !important;
    color: rgba(255, 255, 255, 0.90) !important;
    box-shadow: none !important;
    font-size: 0.78rem !important;
    font-weight: 700 !important;
  }

  .fc-button-primary:hover {
    background: rgba(255, 255, 255, 0.07) !important;
  }

  .fc-button-primary:not(:disabled).fc-button-active {
    background: rgba(111, 91, 255, 0.18) !important;
    border-color: rgba(111, 91, 255, 0.30) !important;
    color: #ffffff !important;
  }

  .fc-button-group {
    gap: 0;
  }

  .fc-button-group > .fc-button {
    border-radius: 0 !important;
  }

  .fc-button-group > .fc-button:first-child {
    border-radius: 8px 0 0 8px !important;
  }

  .fc-button-group > .fc-button:last-child {
    border-radius: 0 8px 8px 0 !important;
  }

  .fc-event {
    cursor: pointer;
    border-radius: 6px;
    padding: 2px 4px;
    color: #ffffff !important;
    font-size: 0.68rem;
    font-weight: 650;
    box-shadow: none;
  }

  .fc-event-title,
  .fc-event-time {
    color: #ffffff !important;
  }

  @media (max-width: 1100px) {
    height: 680px;
  }
`;

const EventsList = styled.div`
  display: grid;
  gap: 0.7rem;
  min-height: 0;
  max-height: calc(100vh - 205px);
  overflow-y: auto;
  padding-right: 0.2rem;

  scrollbar-width: thin;
`;

const EventCard = styled.button`
  position: relative;
  width: 100%;
  padding: 0.95rem 0.95rem 0.95rem 1.05rem;
  overflow: hidden;

  text-align: left;
  color: #f7f7f7;

  border-radius: 12px;
  border: 1px solid
    ${({ $active, $accent }) =>
      $active
        ? `${$accent}88`
        : "rgba(255, 255, 255, 0.09)"};
  border-left: 3px solid ${({ $accent }) => $accent};

  background:
    ${({ $active, $accent }) =>
      $active
        ? `linear-gradient(
            135deg,
            ${$accent}14,
            rgba(25, 36, 57, 0.94)
          )`
        : "rgba(20, 31, 49, 0.82)"};

  cursor: pointer;

  transition:
    border-color 150ms ease,
    background 150ms ease,
    transform 150ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ $accent }) => `${$accent}88`};
  }

  &:focus-visible {
    outline: 2px solid ${({ $accent }) => $accent};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const EventTopLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.7rem;
`;

const EventTitleGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  min-width: 0;

  strong {
    color: rgba(255, 255, 255, 0.96);
    font-size: 0.86rem;
    line-height: 1.28;
  }
`;

const ParticipantsBadge = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;

  min-height: 28px;
  padding: 0.25rem 0.48rem;

  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;

  background: rgba(255, 255, 255, 0.025);
  color: rgba(255, 255, 255, 0.92);

  font-size: 0.74rem;
  font-weight: 750;
`;

const MutedText = styled.p`
  margin: 0.35rem 0 0;
  color: rgba(247, 247, 247, 0.60);
  font-size: 0.76rem;
`;

const ImportLabel = styled.label`
  min-height: 32px;
  padding: 0.35rem 0.62rem;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.42rem;

  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.14);

  background: rgba(255, 255, 255, 0.025);
  color: rgba(247, 247, 247, 0.86);

  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;

  transition:
    background 140ms ease,
    border-color 140ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.22);
  }

  input {
    display: none;
  }
`;

const IconButton = styled(AdminButton)`
  width: 38px;
  min-width: 38px;
  height: 38px;
  padding: 0;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border-radius: 9px;
  border: 1px solid rgba(255, 79, 163, 0.16);

  background: rgba(255, 79, 163, 0.13);
  color: #ff4fa3;

  opacity: ${({ disabled }) => (disabled ? 0.45 : 1)};

  .is-loading {
    animation: calendar-spin 900ms linear infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .is-loading {
      animation: none;
    }
  }

  @keyframes calendar-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SecondaryButton = styled(AdminButton)`
  width: 100%;
  min-height: 34px;
  margin-top: 0.85rem;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.45rem;

  border-radius: 8px;
  border: 1px solid ${({ theme }) =>
    theme?.colors?.border || "rgba(255, 79, 163, 0.35)"};

  background: rgba(255, 79, 163, 0.045);
  color: #ff65ae;

  font-size: 0.76rem;
  font-weight: 750;
`;

const RegistrationsCard = styled.div`
  margin-top: 0.2rem;
  padding: 0.9rem;

  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.09);

  background: rgba(15, 26, 43, 0.74);
`;

const RegistrationsHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;

  color: rgba(255, 255, 255, 0.96);
  font-size: 0.84rem;
  font-weight: 800;
`;

const RegistrationCount = styled.span`
  min-width: 24px;
  padding: 0.16rem 0.42rem;

  border-radius: 999px;

  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;

  text-align: center;
  font-size: 0.7rem;
`;

const SelectedEventTitle = styled.div`
  margin-top: 0.55rem;

  color: rgba(255, 255, 255, 0.88);
  font-size: 0.75rem;
  font-weight: 650;
`;

const RegistrationParticipants = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
`;

const RegistrationContact = styled.span`
  opacity: 0.72;
  overflow-wrap: anywhere;
`;

const CalendarHeader = styled(AdminSectionHeader)`
  align-items: flex-start;
`;

const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  min-width: 0;
`;

const CalendarHeaderActions = styled(AdminActions)`
  margin-top: 0;
  margin-left: auto;
  gap: 0.45rem;

  > button {
    min-height: 32px;
    padding: 0.35rem 0.62rem;
    border-color: rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.025);
    color: rgba(247, 247, 247, 0.86);
    box-shadow: none;
  }

  > button:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.22);
  }

  @media (max-width: 760px) {
    flex-wrap: wrap;
  }
`;

const HeaderIconButton = styled(AdminButton)`
  width: 32px;
  min-width: 32px;
  height: 32px;
  padding: 0;

  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const HeaderCopy = styled.div`
  display: grid;
  gap: 0.28rem;
  min-width: 0;
`;

const HeaderDescription = styled.p`
  margin: 0.08rem 0 0;

  color: rgba(247, 247, 247, 0.62);

  font-size: 0.78rem;
  line-height: 1.4;
`;

const CategoryDot = styled.span`
  flex: 0 0 auto;

  width: 8px;
  height: 8px;
  margin-top: 0.28rem;

  border-radius: 999px;

  background: ${({ $color }) => $color};
  box-shadow: 0 0 0 3px ${({ $color }) => `${$color}18`};
`;

const EventMetadataRow = styled.div`
  margin-top: 0.62rem;

  display: flex;
  align-items: center;
  gap: 0.5rem;

  color: rgba(247, 247, 247, 0.72);

  font-size: 0.76rem;

  svg {
    flex: 0 0 auto;
    color: rgba(247, 247, 247, 0.58);
  }
`;

const EventDate = styled(EventMetadataRow)``;

const EventTime = styled(EventMetadataRow)`
  margin-top: 0.38rem;
`;

const ContextualHint = styled.div`
  margin-top: 0.75rem;
  padding: 0.85rem 0.9rem;

  display: flex;
  align-items: flex-start;
  gap: 0.7rem;

  border-radius: 10px;
  border: 1px solid rgba(79, 156, 249, 0.13);

  background: rgba(79, 156, 249, 0.045);

  color: rgba(247, 247, 247, 0.68);

  font-size: 0.72rem;
  line-height: 1.45;

  svg {
    flex: 0 0 auto;
    color: #4f9cf9;
  }
`;
